import React, { useEffect } from 'react';
import { Outlet, Route, Routes, useNavigate } from 'react-router-dom';
import { useIdentityContext } from '@99designs/design-services-common';
import { FullPageSpinner } from '@99designs/design-services-layouts';
import { PAGE_TRACKING_CONFIG, PageProvider } from '@99designs/tracking';
import { ErrorPage, NotFoundErrorPage } from '../Error';
import { Brief } from '../routes/Brief';
import { ChatBrief } from '../routes/ChatBrief';
import { Create } from '../routes/Create';
import { Review } from '../routes/Review';
import Start from '../routes/Start';
import { SubmitRoute } from '../routes/SubmitRoute';

function SignOut() {
    const identity = useIdentityContext();
    const navigate = useNavigate();

    useEffect(() => {
        identity.SignOut();
        navigate('/');
    });

    return <FullPageSpinner />;
}

export function AppRoutes({ currentCulture }: { currentCulture: string }) {
    const innerRoutes = (
        <>
            <Route path="start" element={<StartRoute currentCulture={currentCulture} />} />
            <Route path="create" element={<Create locale={currentCulture} />} />
            <Route path=":jobId" element={<BriefRoute currentCulture={currentCulture} />} />
            <Route
                path=":jobId/chat"
                element={<ChatBriefRoute currentCulture={currentCulture} />}
            />
            <Route path=":jobId/submit" element={<SubmitRoute currentCulture={currentCulture} />} />
            <Route path=":jobId/review" element={<ReviewRoute currentCulture={currentCulture} />} />
            <Route path="error" element={<ErrorPage />} />

            {process.env.NODE_ENV === 'development' && (
                <Route path="sign-out" element={<SignOut />}></Route>
            )}

            <Route path="*" element={<NotFoundErrorPage />} />
        </>
    );

    return (
        <Routes>
            <Route path={'/experts/request/brief'}>{innerRoutes}</Route>
            <Route path={':lang/experts/request/brief'}>{innerRoutes}</Route>
        </Routes>
    );
}

export function StartRoute({
    currentCulture,
    isUbik,
}: {
    currentCulture: string;
    isUbik?: boolean;
}) {
    return (
        <PageProvider
            locale={currentCulture}
            pageData={{
                pageName: 'Design Services:Expert Services Brief Start',
                ...PAGE_TRACKING_CONFIG['design'],
            }}
        >
            <Start isUbik={isUbik} />
        </PageProvider>
    );
}

export function ChatBriefRoute({ currentCulture }: { currentCulture: string }) {
    return (
        <PageProvider
            locale={currentCulture}
            pageData={{
                pageName: 'Design Services:Expert Services Chat Brief',
                ...PAGE_TRACKING_CONFIG['design'],
            }}
        >
            <ChatBrief />
        </PageProvider>
    );
}

function BriefRoute({ currentCulture }: { currentCulture: string }) {
    return (
        <PageProvider
            locale={currentCulture}
            pageData={{
                pageName: 'Design Services:Expert Services Multi-Step Brief',
                ...PAGE_TRACKING_CONFIG['design'],
            }}
        >
            <Brief />
        </PageProvider>
    );
}

function ReviewRoute({ currentCulture }: { currentCulture: string }) {
    return (
        <PageProvider
            locale={currentCulture}
            pageData={{
                pageName: 'Design Services:Expert Services Brief Review',
                ...PAGE_TRACKING_CONFIG['design'],
            }}
        >
            <Review />
            <Outlet />
        </PageProvider>
    );
}
